import api from "../api";

type ApiResponse<T> = {
  statusCode: number;
  message?: string;
  data?: T;
  error?: string | null;
};

export type CreatePartsTranslationQueuePayload = {
  masterAssemblyId: number;
  languageCode: string;
  vendor?: string;
};

export type PartsTranslationQueueItem = {
  queueId: number;
  masterAssemblyId: number;
  languageCode: string;
  vendor?: string | null;
  status?: string | null;
  createdOn?: string | null;
};

export type PartsTranslationLatestDto = {
  id: number;
  languageCode: string;
  vendor?: string | null;
  createdOn?: string | null;
  latestCompletedOn?: string | null;
  translatedPartCount?: number | null;
  totalPartCount?: number | null;
  latestQueueId?: number | null;
  latestQueueStatus?: string | null;
  latestQueueCreatedOn?: string | null;
  latestQueueStartedOn?: string | null;
  latestQueueCompletedOn?: string | null;
  latestQueueErrorMessage?: string | null;
  hasNewerAttemptAfterCompleted?: boolean;
};

export async function createPartsTranslationQueue(
  oem: string,
  payload: CreatePartsTranslationQueuePayload
): Promise<PartsTranslationQueueItem> {
  if (!oem?.trim()) {
    throw new Error("OEM is required.");
  }
  if (!payload?.masterAssemblyId || Number.isNaN(payload.masterAssemblyId)) {
    throw new Error("Valid master assembly id is required.");
  }
  if (!payload?.languageCode?.trim()) {
    throw new Error("Language code is required.");
  }

  const body: CreatePartsTranslationQueuePayload = {
    masterAssemblyId: payload.masterAssemblyId,
    languageCode: payload.languageCode.trim().toLowerCase(),
    vendor: payload.vendor ?? "partsgptto",
  };

  const { data } = await api.post<ApiResponse<PartsTranslationQueueItem>>(
    `/Parts/translate-descriptions/${oem}`,
    body
  );

  const isSuccess = Boolean(
    data?.statusCode && data.statusCode >= 200 && data.statusCode < 300
  );

  if (isSuccess && data.data) {
    return data.data;
  }

  throw new Error(
    data?.message || data?.error || "Failed to create parts translation queue."
  );
}

export async function fetchLatestPartsTranslationsByMasterAssembly(
  oem: string,
  masterAssemblyId: number
): Promise<PartsTranslationLatestDto[]> {
  if (!oem?.trim()) {
    throw new Error("OEM is required.");
  }
  if (!masterAssemblyId || Number.isNaN(masterAssemblyId)) {
    throw new Error("Valid master assembly id is required.");
  }

  const { data } = await api.get<ApiResponse<PartsTranslationLatestDto[]>>(
    `/Parts/${oem}/translations/latest/by-master-assembly/${masterAssemblyId}`
  );

  if (data?.statusCode === 200 && Array.isArray(data.data)) {
    return data.data;
  }

  if (data?.statusCode === 204) {
    return [];
  }

  throw new Error(
    data?.message || data?.error || "Failed to load parts translations."
  );
}
